/**
 * Merchant Storefront management — iter 44 · Phase A & Growth multi-storefront.
 * Reads/writes /api/v1/merchant/storefront with per-storefront ?sid= support.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Store, Save, Rocket, PauseCircle, PlayCircle, FileEdit, ImageIcon, Globe, Smartphone, Sparkles, BarChart3, Wand2, ArrowRight, Loader2, Check, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAiEntitlement } from "@/hooks/useAiEntitlement";
import { AiUpgradeCta } from "@/components/merchant/AiUpgrade";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const CATEGORIES = ["retail", "fashion", "food", "beauty", "electronics", "services", "travel", "grocery", "home", "digital", "other"];

export default function MerchantStorefront() {
  const [searchParams, setSearchParams] = useSearchParams();
  const sidParam = searchParams.get("sid");

  const [sf, setSf] = useState(null);
  const [storefrontsList, setStorefrontsList] = useState([]);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState("details");
  const [analytics, setAnalytics] = useState(null);
  const [showAI, setShowAI] = useState(false);
  const [showGenerator, setShowGenerator] = useState(false);
  const [genUrl, setGenUrl] = useState("");
  const [genLoading, setGenLoading] = useState(false);
  const [genDraft, setGenDraft] = useState(null);
  const [autoLaunching, setAutoLaunching] = useState(false);
  const [launchedOffer, setLaunchedOffer] = useState(null);
  const { aiEnabled } = useAiEntitlement();

  const runGenerator = async () => {
    if (!genUrl) return;
    setGenLoading(true);
    setGenDraft(null);
    setLaunchedOffer(null);
    try {
      const r = await api.post("/v1/merchant/storefront/generate", { url: genUrl });
      setGenDraft(r.data.draft);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not analyse that URL");
    } finally { setGenLoading(false); }
  };

  const autoLaunchOffer = async () => {
    if (!genDraft?.suggested_offer) return;
    setAutoLaunching(true);
    try {
      const so = genDraft.suggested_offer;
      const r = await api.post("/v1/merchant/storefront/auto-launch-offer", {
        name: so.name,
        discount_type: so.discount_type === "fixed" ? "fixed" : "percent",
        discount_value: so.discount_value,
        description: so.description || "",
        validity_days: 30,
      });
      setLaunchedOffer(r.data.campaign);
      toast.success("Launch offer created — awaiting admin approval.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not launch offer");
    } finally { setAutoLaunching(false); }
  };

  const acceptGenerator = () => {
    if (!genDraft) return;
    patch("name", genDraft.name || sf.name);
    patch("short_description", genDraft.short_description || sf.short_description);
    patch("long_description", genDraft.long_description || sf.long_description);
    patch("primary_category", genDraft.primary_category || sf.primary_category);
    if (genDraft.banner_image_suggested) patch("banner_image", genDraft.banner_image_suggested);
    if (genDraft.website_url) patch("website_url", genDraft.website_url);
    toast.success("Draft applied. Review and click Save to persist.");
    setShowGenerator(false);
    setGenDraft(null);
    setGenUrl("");
  };

  const reload = useCallback(async () => {
    try {
      // First load the full list of storefronts
      const listRes = await api.get("/v1/merchant/storefronts");
      const sfs = listRes.data?.storefronts || [];
      setStorefrontsList(sfs);

      const targetSid = sidParam || (sfs[0]?.id);
      const url = targetSid ? `/v1/merchant/storefront?sid=${encodeURIComponent(targetSid)}` : "/v1/merchant/storefront";
      const r = await api.get(url);
      setSf(r.data);
      setErr(null);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not load storefront");
    }
  }, [sidParam]);

  useEffect(() => { reload(); }, [reload]);

  const switchStorefront = (newSid) => {
    setSearchParams({ sid: newSid });
    setAnalytics(null);
  };

  const loadAnalytics = useCallback(async () => {
    if (analytics || !sf?.id) return;
    try {
      const r = await api.get(`/v1/merchant/storefront/analytics?sid=${encodeURIComponent(sf.id)}&days=30`);
      setAnalytics(r.data);
    } catch (e) {
      toast.error("Could not load analytics");
    }
  }, [analytics, sf?.id]);

  useEffect(() => { if (tab === "analytics") loadAnalytics(); }, [tab, loadAnalytics]);

  const patch = (k, v) => setSf((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        name: sf.name, short_description: sf.short_description,
        long_description: sf.long_description, primary_category: sf.primary_category,
        logo_url: sf.logo_url, banner_image: sf.banner_image,
        website_url: sf.website_url, app_url: sf.app_url,
        purchase_destination_type: sf.purchase_destination_type || "WEBSITE",
        online_offline_type: sf.online_offline_type, country: sf.country,
        province: sf.province, city: sf.city,
      };
      const r = await api.patch(`/v1/merchant/storefront?sid=${encodeURIComponent(sf.id)}`, body);
      setSf(r.data);
      toast.success(`Storefront “${sf.name}” updated.`);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.detail || d?.message || "Save failed");
    } finally { setSaving(false); }
  };

  const publish = async () => {
    setPublishing(true);
    try {
      const r = await api.post(`/v1/merchant/storefront/publish?sid=${encodeURIComponent(sf.id)}`);
      setSf(r.data);
      toast.success(`“${sf.name}” is now LIVE.`);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.detail || "Publish failed");
    } finally { setPublishing(false); }
  };

  const pause = async () => {
    try {
      const r = await api.post(`/v1/merchant/storefront/pause?sid=${encodeURIComponent(sf.id)}`);
      setSf(r.data);
      toast.success(`“${sf.name}” paused.`);
    } catch { toast.error("Pause failed"); }
  };

  const setDraft = async () => {
    try {
      const r = await api.post(`/v1/merchant/storefront/draft?sid=${encodeURIComponent(sf.id)}`);
      setSf(r.data);
      toast.success(`“${sf.name}” moved to DRAFT.`);
    } catch { toast.error("Setting draft failed"); }
  };

  if (err && !sf) return <div className="p-8 text-sm text-rose-700" data-testid="storefront-error">{typeof err === "string" ? err : "Error"}</div>;
  if (!sf) return <div className="p-8 text-sm text-slate-500" data-testid="storefront-loading">Loading storefront…</div>;

  const statusTint = sf.status === "LIVE" ? "bg-emerald-100 text-emerald-800 border-emerald-200" :
                     sf.status === "PAUSED" ? "bg-amber-100 text-amber-800 border-amber-200" :
                     sf.status === "SUSPENDED" ? "bg-rose-100 text-rose-800 border-rose-200" :
                     "bg-slate-100 text-slate-700 border-slate-200";

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto" data-testid="merch-storefront">
      {/* Multi-Storefront Switcher (Rule for Growth and multi-storefront accounts) */}
      {storefrontsList.length > 1 && (
        <div className="pf-card p-3.5 bg-gradient-to-r from-slate-50 via-cyan-50/40 to-slate-50 border-cyan-200/80 flex items-center justify-between gap-4 flex-wrap" data-testid="sf-multi-switcher">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-md bg-cyan-100 text-cyan-800 flex items-center justify-center font-semibold text-xs">
              <Store className="w-4 h-4" />
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-900 flex items-center gap-1.5">
                Active Storefront ({storefrontsList.findIndex((s) => s.id === sf.id) + 1} of {storefrontsList.length})
              </div>
              <div className="text-[11px] text-slate-500">
                Growth plan allows managing up to 3 storefronts individually.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <select
                value={sf.id}
                onChange={(e) => switchStorefront(e.target.value)}
                className="bg-white border border-slate-300 rounded-lg px-3 py-1.5 pr-8 text-xs font-medium text-slate-800 focus:ring-2 focus:ring-cyan-500 appearance-none shadow-sm cursor-pointer"
                data-testid="sf-select-dropdown"
              >
                {storefrontsList.map((s, idx) => (
                  <option key={s.id} value={s.id}>
                    {idx + 1}. {s.name} ({s.status || "DRAFT"})
                  </option>
                ))}
              </select>
              <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-2.5 pointer-events-none" />
            </div>
            <Link
              to="/merchant/storefronts"
              className="text-xs font-semibold text-cyan-700 hover:text-cyan-900 inline-flex items-center gap-1"
              data-testid="sf-view-all-link"
            >
              All Storefronts →
            </Link>
          </div>
        </div>
      )}

      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="pf-chip pf-chip-cyan w-fit"><Store className="w-3 h-3" /> My storefront</div>
          <h1 className="font-display text-2xl md:text-3xl text-slate-900 mt-2">{sf.name}</h1>
          <div className="text-xs text-slate-500 mt-1">Store id · <span className="font-mono">{sf.store_id}</span> · SID · <span className="font-mono">{sf.id}</span></div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-widest font-semibold border ${statusTint}`}
                data-testid="storefront-status">{sf.status}</span>
          {sf.status === "LIVE" && (
            <>
              <button onClick={pause} className="px-3 py-1.5 rounded-full text-xs border border-amber-200 text-amber-800 hover:bg-amber-50 inline-flex items-center gap-1 font-medium" data-testid="storefront-pause">
                <PauseCircle className="w-3 h-3" /> Pause
              </button>
              <button onClick={setDraft} className="px-3 py-1.5 rounded-full text-xs border border-slate-200 text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1 font-medium" data-testid="storefront-draft-btn">
                <FileEdit className="w-3 h-3" /> Set to Draft
              </button>
            </>
          )}
          {sf.status === "PAUSED" && (
            <>
              <button onClick={publish} disabled={publishing}
                      className="pf-btn-primary px-4 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-60"
                      data-testid="storefront-publish">
                <PlayCircle className="w-3 h-3" /> {publishing ? "Resuming…" : "Resume"}
              </button>
              <button onClick={setDraft} className="px-3 py-1.5 rounded-full text-xs border border-slate-200 text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1 font-medium" data-testid="storefront-draft-btn">
                <FileEdit className="w-3 h-3" /> Set to Draft
              </button>
            </>
          )}
          {sf.status !== "LIVE" && sf.status !== "PAUSED" && (
            <button onClick={publish} disabled={publishing}
                    className="pf-btn-primary px-4 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-60"
                    data-testid="storefront-publish">
              <Rocket className="w-3 h-3" /> {publishing ? "Publishing…" : "Publish"}
            </button>
          )}
        </div>
      </header>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile label="Best offer" value={sf.display_label || "—"} testid="storefront-kpi-best" tone="from-emerald-50 to-emerald-100/60" accent="text-emerald-700" />
        <KpiTile label="Active offers" value={sf.active_offer_count || 0} testid="storefront-kpi-offers" tone="from-cyan-50 to-cyan-100/60" accent="text-cyan-700" />
        <KpiTile label="Primary category" value={sf.primary_category || "—"} testid="storefront-kpi-cat" tone="from-violet-50 to-violet-100/60" accent="text-violet-700" />
        <KpiTile label="Highest discount" value={sf.highest_discount_pct ? `${sf.highest_discount_pct}%` : "—"} testid="storefront-kpi-pct" tone="from-slate-50 to-white" accent="text-slate-700" />
      </div>


      {/* Preview banner */}
      <div className="rounded-2xl overflow-hidden border border-slate-200 relative bg-slate-100" data-testid="storefront-preview">
        <div className="h-40 relative"
             style={{ backgroundImage: sf.banner_image ? `url(${sf.banner_image})` : undefined,
                      backgroundSize: "cover", backgroundPosition: "center",
                      background: sf.banner_image ? undefined : "linear-gradient(135deg,#0e7490 0%,#67e8f9 100%)" }}>
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          <div className="absolute bottom-3 left-4 text-white">
            <div className="font-display text-xl">{sf.name}</div>
            {sf.display_label && <div className="text-xs text-cyan-200 font-semibold">{sf.display_label} · {sf.active_offer_count} active offers</div>}
          </div>
          {sf.logo_url && (
            <img src={sf.logo_url} alt="" className="absolute top-3 left-4 w-12 h-12 rounded-lg bg-white p-1 shadow" />
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200" data-testid="storefront-tabs">
        <TabBtn active={tab === "details"} onClick={() => setTab("details")} icon={Store} testid="storefront-tab-details">Details</TabBtn>
        <TabBtn active={tab === "analytics"} onClick={() => setTab("analytics")} icon={BarChart3} testid="storefront-tab-analytics">Analytics</TabBtn>
      </div>

      {tab === "analytics" ? (
        <AnalyticsPanel data={analytics} onReload={() => { setAnalytics(null); loadAnalytics(); }} />
      ) : (
      <>
      {/* AI-prompt CTA above the editor */}
      <div className="pf-card p-5 bg-gradient-to-r from-violet-50 via-cyan-50 to-emerald-50 border-cyan-200/60 flex items-center gap-4"
           data-testid="storefront-ai-cta">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-violet-500 to-cyan-500 text-white flex items-center justify-center shrink-0">
          <Wand2 className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-slate-900 text-lg">Create an offer with AI</div>
          <div className="text-xs text-slate-600 mt-0.5">Describe the promo in plain English — Cyannect turns it into a structured draft you can review.</div>
        </div>
        {aiEnabled ? (
        <button onClick={() => setShowAI(true)}
                className="pf-btn-primary px-4 py-2 rounded-full text-xs font-semibold inline-flex items-center gap-1"
                data-testid="storefront-ai-open">
          <Sparkles className="w-3 h-3" /> Draft an offer
        </button>
        ) : <AiUpgradeCta testid="storefront-ai-locked" />}
      </div>

      {/* AI Storefront Generator CTA — paste URL, get whole storefront draft */}
      <div className="pf-card p-5 bg-gradient-to-r from-emerald-50 via-cyan-50 to-violet-50 border-emerald-200/60 flex items-center gap-4"
           data-testid="storefront-gen-cta">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-500 text-white flex items-center justify-center shrink-0">
          <Globe className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-slate-900 text-lg">Generate from your website</div>
          <div className="text-xs text-slate-600 mt-0.5">Paste your store URL — AI scrapes the page and drafts your name, description, category, banner, and a launch offer.</div>
        </div>
        {aiEnabled ? (
        <button onClick={() => setShowGenerator(true)}
                className="pf-btn-primary px-4 py-2 rounded-full text-xs font-semibold inline-flex items-center gap-1"
                data-testid="storefront-gen-open">
          <Sparkles className="w-3 h-3" /> Generate storefront
        </button>
        ) : <AiUpgradeCta testid="storefront-gen-locked" />}
      </div>

      {/* AI Storefront Generator modal */}
      <Dialog open={showGenerator} onOpenChange={setShowGenerator}>
        <DialogContent className="max-w-2xl" data-testid="storefront-gen-modal">
          <DialogHeader>
            <DialogTitle>Generate a storefront from your website</DialogTitle>
            <DialogDescription>
              Paste your public URL — Cyannect scrapes the page and drafts every field. Nothing is saved until you review and Accept.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="flex gap-2">
              <input value={genUrl} onChange={(e) => setGenUrl(e.target.value)}
                     placeholder="https://your-brand.com"
                     className="input flex-1"
                     data-testid="storefront-gen-url" />
              <button onClick={runGenerator} disabled={genLoading || !genUrl}
                      className="pf-btn-primary px-4 py-2 rounded-full text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-60"
                      data-testid="storefront-gen-run">
                {genLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                {genLoading ? "Analysing…" : "Analyse"}
              </button>
            </div>
            {genDraft && (
              <div className="pf-card p-4 space-y-3 bg-slate-50" data-testid="storefront-gen-draft">
                {genDraft.banner_image_suggested && (
                  <img src={genDraft.banner_image_suggested} alt="" className="h-28 w-full object-cover rounded" />
                )}
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div><b className="block text-slate-500">Brand name</b>{genDraft.name}</div>
                  <div><b className="block text-slate-500">Category</b>{genDraft.primary_category}</div>
                  <div className="col-span-2"><b className="block text-slate-500">Short description</b>{genDraft.short_description}</div>
                  <div className="col-span-2"><b className="block text-slate-500">Long description</b>{genDraft.long_description}</div>
                </div>
                {genDraft.suggested_offer && (
                  <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 space-y-2">
                    <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Suggested launch offer</div>
                    <div className="text-sm font-semibold text-slate-900">{genDraft.suggested_offer.name}</div>
                    <div className="text-xs text-slate-700">{genDraft.suggested_offer.description}</div>
                    <div className="text-[10px] text-slate-500">{genDraft.suggested_offer.discount_value}{genDraft.suggested_offer.discount_type === "percent" ? "%" : " CAD"} off</div>
                    {launchedOffer ? (
                      <div className="pt-2 border-t border-emerald-200 flex items-center gap-2 text-xs text-emerald-800"
                            data-testid="storefront-launched-badge">
                        <span className="pf-chip pf-chip-amber text-[10px]">pending review</span>
                        Offer created and queued for Cyannect admin approval.
                      </div>
                    ) : (
                      <button onClick={autoLaunchOffer} disabled={autoLaunching}
                              className="pf-btn-primary px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-60"
                              data-testid="storefront-auto-launch">
                        {autoLaunching ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                        Create this offer in one click
                      </button>
                    )}
                  </div>
                )}
                <div className="flex gap-2 justify-end pt-2">
                  <button onClick={() => setGenDraft(null)}
                          className="pf-btn-glass px-4 py-2 rounded-full text-xs font-semibold"
                          data-testid="storefront-gen-discard">Discard</button>
                  <button onClick={acceptGenerator}
                          className="pf-btn-primary px-4 py-2 rounded-full text-xs font-semibold inline-flex items-center gap-1"
                          data-testid="storefront-gen-accept">
                    <ArrowRight className="w-3 h-3" /> Apply to storefront
                  </button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Editor */}
      <div className="pf-card p-6 space-y-4">
        <h2 className="font-display text-lg text-slate-900">Storefront details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Storefront name" testid="storefront-name">
            <input value={sf.name || ""} onChange={(e) => patch("name", e.target.value)} className="input" data-testid="storefront-name-input" />
          </Field>
          <Field label="Primary category" testid="storefront-cat">
            <select value={sf.primary_category || ""} onChange={(e) => patch("primary_category", e.target.value)} className="input" data-testid="storefront-cat-input">
              <option value="">Choose…</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Logo URL" testid="storefront-logo">
            <div className="flex gap-2 items-center">
              <ImageIcon className="w-4 h-4 text-slate-400" />
              <input value={sf.logo_url || ""} onChange={(e) => patch("logo_url", e.target.value)} className="input" placeholder="https://…" data-testid="storefront-logo-input" />
            </div>
          </Field>
          <Field label="Banner image URL" testid="storefront-banner">
            <div className="flex gap-2 items-center">
              <ImageIcon className="w-4 h-4 text-slate-400" />
              <input value={sf.banner_image || ""} onChange={(e) => patch("banner_image", e.target.value)} className="input" placeholder="https://…" data-testid="storefront-banner-input" />
            </div>
          </Field>
          <Field label="Website" testid="storefront-web">
            <div className="flex gap-2 items-center"><Globe className="w-4 h-4 text-slate-400" />
              <input value={sf.website_url || ""} onChange={(e) => patch("website_url", e.target.value)} className="input" placeholder="https://brand.com" data-testid="storefront-web-input" />
            </div>
          </Field>
          <Field label="Where customers pay" testid="storefront-destination">
            <select
              value={sf.purchase_destination_type || "WEBSITE"}
              onChange={(e) => patch("purchase_destination_type", e.target.value)}
              className="input"
              data-testid="storefront-destination-input"
            >
              <option value="WEBSITE">Website only</option>
              <option value="WEBSITE_AND_APP">Website + mobile app</option>
              <option value="MOBILE_APP">Mobile app only</option>
            </select>
          </Field>
          <Field label="Mobile app link (optional)" testid="storefront-app">
            <div className="flex gap-2 items-center"><Smartphone className="w-4 h-4 text-slate-400" />
              <input value={sf.app_url || ""} onChange={(e) => patch("app_url", e.target.value)} className="input" placeholder="Only if you have an app — deep-link or store URL" data-testid="storefront-app-input" />
            </div>
          </Field>
          <Field label="Channel" testid="storefront-channel">
            <div className="input bg-slate-50 text-slate-700" data-testid="storefront-channel-input">Online only</div>
            <p className="text-xs text-slate-500 mt-1" data-testid="storefront-channel-note">Cyannect currently supports online stores with their own checkout. Physical-store coupons aren't available.</p>
          </Field>
          <Field label="Country" testid="storefront-country">
            <input value={sf.country || ""} onChange={(e) => patch("country", e.target.value)} className="input" placeholder="CA / US" data-testid="storefront-country-input" />
          </Field>
        </div>
        <Field label="Short tagline" testid="storefront-short">
          <input value={sf.short_description || ""} onChange={(e) => patch("short_description", e.target.value)} className="input"
                 placeholder="Sportswear & footwear for every runner" data-testid="storefront-short-input" />
        </Field>
        <Field label="Long description" testid="storefront-long">
          <textarea value={sf.long_description || ""} onChange={(e) => patch("long_description", e.target.value)} rows={4} className="input" data-testid="storefront-long-input" />
        </Field>
        <div className="flex justify-end">
          <button onClick={save} disabled={saving}
                  className="pf-btn-primary px-5 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60"
                  data-testid="storefront-save">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      <div className="text-xs text-slate-500 flex items-center gap-2">
        <Sparkles className="w-3 h-3 text-cyan-500" />
        Best offer & active offer count refresh automatically every time you create, pause or expire an offer.
      </div>

      <style>{`.input { width:100%; padding:.55rem .75rem; border:1px solid #e2e8f0; border-radius:.5rem; font-size:.875rem; background:#fff; } .input:focus { outline: 2px solid #06b6d4; outline-offset: -1px; }`}</style>
      </>
      )}

      <PromptOfferModal open={showAI} onOpenChange={setShowAI} storefrontId={sf.id} />
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
            className={`px-4 py-2 text-sm font-semibold inline-flex items-center gap-1.5 border-b-2 -mb-px transition
              ${active ? "border-cyan-500 text-cyan-800" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
      <Icon className="w-3.5 h-3.5" /> {children}
    </button>
  );
}

function AnalyticsPanel({ data, onReload }) {
  if (!data) return <div className="p-8 text-sm text-slate-500" data-testid="storefront-analytics-loading">Loading analytics…</div>;
  const k = data.kpis || {};
  return (
    <div className="space-y-4" data-testid="storefront-analytics">
      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-500">Last {data.window_days} days</div>
        <button onClick={onReload} className="text-xs text-cyan-700 hover:text-cyan-900 font-semibold inline-flex items-center gap-1"
                data-testid="storefront-analytics-refresh">Refresh</button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <A label="Storefront views" value={(k.storefront_views || 0).toLocaleString()} tone="from-cyan-50 to-cyan-100/60" accent="text-cyan-700" testid="analytics-views" />
        <A label="Offer clicks" value={(k.offer_clicks || 0).toLocaleString()} tone="from-violet-50 to-violet-100/60" accent="text-violet-700" testid="analytics-clicks" />
        <A label="Offer activations" value={(k.offer_activations || 0).toLocaleString()} tone="from-emerald-50 to-emerald-100/60" accent="text-emerald-700" testid="analytics-activations" />
        <A label="Conversions" value={(k.conversions || 0).toLocaleString()} tone="from-amber-50 to-amber-100/60" accent="text-amber-700" testid="analytics-conversions" />
        <A label="Attributed revenue" value={k.attributed_revenue_display} tone="from-emerald-50 to-emerald-100/60" accent="text-emerald-800" testid="analytics-revenue" />
        <A label="Cashback issued" value={k.cashback_issued_display} tone="from-rose-50 to-rose-100/60" accent="text-rose-700" testid="analytics-cashback" />
        <A label="Products" value={(k.products || 0).toLocaleString()} tone="from-slate-50 to-white" accent="text-slate-700" testid="analytics-products" />
        <A label="Active offers" value={(k.active_offers || 0).toLocaleString()} tone="from-slate-50 to-white" accent="text-slate-700" testid="analytics-active-offers" />
      </div>

      <div className="pf-card p-5" data-testid="analytics-top-offers">
        <div className="text-sm font-display text-slate-900 mb-3">Top offers by activation</div>
        {(data.top_offers || []).length === 0 ? (
          <div className="text-xs text-slate-500">No activations yet.</div>
        ) : (
          <div className="space-y-1">
            {(data.top_offers || []).map((o) => (
              <div key={o.id} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0 text-sm">
                <div className="truncate">
                  <div className="text-slate-900 font-medium">{o.name}</div>
                  <div className="text-xs text-slate-500">{o.discount_value}{o.discount_type === "percent" ? "%" : ""} · {o.views || 0} views</div>
                </div>
                <div className="text-emerald-700 font-semibold tabular-nums">{o.redemptions || 0} activations</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function A({ label, value, tone, accent, testid }) {
  return (
    <div className={`pf-card p-4 bg-gradient-to-br ${tone}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className={`font-display text-2xl font-medium mt-1.5 tabular-nums ${accent}`}>{value}</div>
    </div>
  );
}

function PromptOfferModal({ open, onOpenChange, storefrontId }) {
  const [prompt, setPrompt] = useState("");
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setPrompt(""); setDraft(null); } }, [open]);

  const generate = async () => {
    if (prompt.length < 8) return toast.error("Describe the offer in a bit more detail.");
    setBusy(true);
    try {
      const r = await api.post("/v1/merchant/offers/draft-from-prompt", { prompt });
      setDraft(r.data.draft);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.detail || "AI could not draft that offer.");
    } finally { setBusy(false); }
  };

  const useIt = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      // Push to the existing merchant campaign endpoint as a DRAFT (§18).
      const payload = {
        name: draft.name,
        campaign_type: draft.discount_type === "cashback" ? "cashback" : "coupon",
        discount_type: draft.discount_type === "cashback" ? "percent" : draft.discount_type,
        discount_value: draft.discount_value,
        commission_rate: 0,
        expires_at: draft.expires_at,
        min_purchase: draft.min_purchase,
        description: draft.description,
      };
      await api.post("/merchant/campaigns", payload);
      toast.success("Draft offer created — review it in Offers.");
      onOpenChange(false);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not save draft — try tweaking the fields.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="ai-offer-modal">
        <DialogHeader>
          <DialogTitle>Draft an offer with AI</DialogTitle>
          <DialogDescription>Cyannect turns your description into a structured offer. You always review before it goes live.</DialogDescription>
        </DialogHeader>
        {!draft ? (
          <>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Describe the promotion</label>
            <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={4}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm mt-1"
                      placeholder='e.g. "15% off running shoes this weekend, minimum $100 order, one per customer"'
                      data-testid="ai-offer-prompt" />
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => onOpenChange(false)} className="px-4 py-2 rounded-full text-sm border border-slate-200 hover:bg-slate-50">Cancel</button>
              <button onClick={generate} disabled={busy}
                      className="pf-btn-primary px-5 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60"
                      data-testid="ai-offer-generate">
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                {busy ? "Thinking…" : "Generate draft"}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="pf-card p-4 space-y-2 bg-gradient-to-br from-violet-50 to-cyan-50 border-cyan-200" data-testid="ai-offer-draft">
              <div className="font-display text-lg text-slate-900">{draft.name}</div>
              <div className="text-xs text-slate-600 italic">{draft.description}</div>
              <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                <Kv k="Discount" v={draft.discount_type === "percent" ? `${draft.discount_value}%` :
                                     draft.discount_type === "fixed" ? `CA$${draft.discount_value} off` :
                                     draft.discount_type} />
                <Kv k="Min purchase" v={draft.min_purchase ? `CA$${draft.min_purchase}` : "—"} />
                <Kv k="Per customer" v={draft.customer_limit || "unlimited"} />
                <Kv k="Validity" v={draft.validity_days ? `${draft.validity_days} days` : "—"} />
                {draft.category && <Kv k="Category" v={draft.category} />}
                <Kv k="AI confidence" v={`${(draft.confidence * 100).toFixed(0)}%`} />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => setDraft(null)} className="px-4 py-2 rounded-full text-sm border border-slate-200 hover:bg-slate-50" data-testid="ai-offer-retry">
                Try again
              </button>
              <button onClick={useIt} disabled={busy}
                      className="pf-btn-primary px-5 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60"
                      data-testid="ai-offer-use">
                {busy ? "Saving…" : "Save as draft"} <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Kv({ k, v }) {
  return (
    <div className="flex justify-between border-b border-cyan-100/60 py-1">
      <span className="text-slate-500">{k}</span>
      <span className="text-slate-900 font-semibold">{v}</span>
    </div>
  );
}

function KpiTile({ label, value, tone, accent, testid }) {
  return (
    <div className={`pf-card p-4 bg-gradient-to-br ${tone}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className={`font-display text-xl md:text-2xl font-medium mt-1.5 ${accent}`}>{value}</div>
    </div>
  );
}

function Field({ label, testid, children }) {
  return (
    <div data-testid={testid}>
      <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
